import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Card, Descriptions, Divider, Drawer, Form, List, message, Modal, Popconfirm, Select, Space, Spin, Tag, Typography, Avatar} from 'antd';
import {DeleteOutlined, EditOutlined, SaveOutlined, UserOutlined, MenuOutlined, ApiOutlined, LinkOutlined} from '@ant-design/icons';
import {createAuthority, deleteAuthority, updateAuthority} from '../api/authorityApi';
import {getGroups} from '../api/groupApi';
import userApi from '../api/userApi';
import {getMenus} from '../api/menuApi';
import {getApis} from '../api/apiApi';
import {pickList} from '../api/http';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';

const {Text} = Typography;

const TYPE_COLOR = {USER_GROUP: 'cyan', MENU_GROUP: 'blue', API_GROUP: 'geekblue'};
const TYPE_LABEL = {USER_GROUP: '사용자그룹', MENU_GROUP: '메뉴그룹', API_GROUP: 'API그룹'};

/**
 * 권한 상세 Drawer
 * - record가 없으면(신규) 혹은 authrtId가 없으면 등록 모드
 * - record.authrtId가 있으면 상세 보기 모드 (initialEditMode=true 이면 수정 모드 시작)
 */
const AuthorityDetailDrawer = ({open, record, initialEditMode = false, onClose, onRegistered, onDelete}) => {
  const [form] = Form.useForm();
  const [editMode, setEditMode] = useState(initialEditMode);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // 그룹 데이터
  const [userGroups, setUserGroups] = useState([]);
  const [menuGroups, setMenuGroups] = useState([]);
  const [apiGroups, setApiGroups] = useState([]);
  const [userLookup, setUserLookup] = useState({});
  const [menuLookup, setMenuLookup] = useState({});
  const [apiLookup, setApiLookup] = useState({});
  const [lookupLoading, setLookupLoading] = useState(false);
  const lookupsLoadedRef = useRef(false);

  const {canCall} = useApiPermission();
  const canCreate = canCall(createAuthority);
  const canUpdate = canCall(updateAuthority);
  const canDelete = canCall(deleteAuthority);

  const isNew = !record?.authrtId;
  const canEdit = isNew ? canCreate : canUpdate;

  // 그룹 목록 로드
  useEffect(() => {
    const load = async () => {
      try {
        const [ugRes, mgRes, agRes] = await Promise.all([
          getGroups('USER_GROUP'),
          getGroups('MENU_GROUP'),
          getGroups('API_GROUP')
        ]);
        setUserGroups(pickList(ugRes));
        setMenuGroups(pickList(mgRes));
        setApiGroups(pickList(agRes));
      } catch (e) {
        console.error('그룹 목록 조회 실패:', e);
      }
    };
    if (open) load();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (lookupsLoadedRef.current) return;

    let ignore = false;

    const loadLookups = async () => {
      setLookupLoading(true);
      try {
        const [usersRes, menusRes, apisRes] = await Promise.all([
          userApi.listUsers().catch((err) => {
            console.error('사용자 목록 조회 실패:', err);
            return null;
          }),
          getMenus().catch((err) => {
            console.error('메뉴 목록 조회 실패:', err);
            return null;
          }),
          getApis().catch((err) => {
            console.error('API 목록 조회 실패:', err);
            return null;
          })
        ]);

        if (ignore) return;

        const users = pickList(usersRes);
        const menus = pickList(menusRes);
        const apis = pickList(apisRes);

        const userMap = {};
        users.forEach((u) => {
          if (u && u.userId) {
            userMap[u.userId] = u;
          }
        });

        const menuMap = {};
        menus.forEach((m) => {
          if (m && m.menuId) {
            menuMap[m.menuId] = m;
          }
        });

        const apiMap = {};
        apis.forEach((a) => {
          if (a && a.apiId) {
            apiMap[a.apiId] = a;
          }
        });

        setUserLookup(userMap);
        setMenuLookup(menuMap);
        setApiLookup(apiMap);
        lookupsLoadedRef.current = true;
      } catch (err) {
        console.error('그룹 대상 정보 조회 실패:', err);
        if (!ignore) {
          setUserLookup({});
          setMenuLookup({});
          setApiLookup({});
        }
      } finally {
        if (!ignore) {
          setLookupLoading(false);
        }
      }
    };

    loadLookups();

    return () => {
      ignore = true;
    };
  }, [open]);

  // record 변경 시 폼 초기화
  useEffect(() => {
    if (!open) return;
    setEditMode(initialEditMode);
    if (record?.authrtId) {
      // 상세 보기 모드
      form.setFieldsValue({
        userGroupId: record.userGroupId,
        trgtGroupType: record.trgtGroupType,
        trgtGroupId: record.trgtGroupId
      });
    } else {
      // 신규 등록 모드
      form.resetFields();
    }
  }, [open, record, initialEditMode, form]);

  const handleSave = useCallback(async (valuesFromForm) => {
    try {
      const values = valuesFromForm ?? await form.validateFields();
      setSaving(true);

      if (isNew) {
        // 신규 등록
        await createAuthority(values);
        message.success('권한이 등록되었습니다.');
      } else {
        // 수정
        await updateAuthority(record.authrtId, values);
        message.success('권한이 수정되었습니다.');
      }

      if (onRegistered) onRegistered();
      onClose();
    } catch (err) {
      if (err?.errorFields) {
        // 유효성 검사 실패
        return;
      }
      console.error('권한 저장 실패:', err);
      message.error(err?.message || '권한 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [form, isNew, record, onRegistered, onClose]);

  const handleEdit = () => {
    if (!canEdit) {
      message.warning('수정 권한이 없습니다.');
      return;
    }
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    if (record?.authrtId) {
      form.setFieldsValue({
        userGroupId: record.userGroupId,
        trgtGroupType: record.trgtGroupType,
        trgtGroupId: record.trgtGroupId
      });
    }
  };

  const handleDelete = () => {
    if (!canDelete) {
      message.warning('삭제 권한이 없습니다.');
      return;
    }
    Modal.confirm({
      title: '권한 삭제',
      content: '이 권한을 삭제하시겠습니까?',
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          await deleteAuthority(record.authrtId);
          message.success('권한이 삭제되었습니다.');
          if (onDelete) onDelete();
          onClose();
        } catch (err) {
          console.error('권한 삭제 실패:', err);
          message.error(err?.message || '권한 삭제에 실패했습니다.');
        }
      }
    });
  };

  const trgtGroupType = Form.useWatch('trgtGroupType', form);
  const selectedUserGroupId = Form.useWatch('userGroupId', form);
  const selectedTrgtGroupId = Form.useWatch('trgtGroupId', form);

  const trgtGroupOptions = useMemo(() => {
    if (trgtGroupType === 'MENU_GROUP') {
      return menuGroups.map(g => ({label: g.groupNm, value: g.groupId}));
    }
    if (trgtGroupType === 'API_GROUP') {
      return apiGroups.map(g => ({label: g.groupNm, value: g.groupId}));
    }
    return [];
  }, [trgtGroupType, menuGroups, apiGroups]);

  // 선택된 그룹 정보
  const selectedUserGroup = useMemo(() => 
    userGroups.find(g => g.groupId === selectedUserGroupId),
    [userGroups, selectedUserGroupId]
  );

  const selectedTrgtGroup = useMemo(() => 
    [...menuGroups, ...apiGroups].find(g => g.groupId === selectedTrgtGroupId),
    [menuGroups, apiGroups, selectedTrgtGroupId]
  );

  const getGroupMembers = useCallback((group) => {
    if (!group) return [];
    const ids = Array.isArray(group.trgtIdList) ? group.trgtIdList : [];
    if (!ids.length) return [];

    if (group.groupType === 'USER_GROUP') {
      return ids.map((rawId) => {
        const id = String(rawId);
        const user = userLookup[id];
        const primary = user?.userNm || user?.lgnUserNm || id;
        const secondaryParts = [];
        if (user?.lgnUserNm && user?.lgnUserNm !== primary) {
          secondaryParts.push(user.lgnUserNm);
        }
        if (user?.eml) {
          secondaryParts.push(user.eml);
        }
        return {
          key: id,
          primary,
          secondary: secondaryParts.join(' • '),
          tag: user?.userId || id,
          tagColor: 'blue'
        };
      });
    }

    if (group.groupType === 'MENU_GROUP') {
      return ids.map((rawId) => {
        const id = String(rawId);
        const menu = menuLookup[id];
        const primary = menu?.menuNm || menu?.path || id;
        const secondary = menu?.path && menu?.path !== primary ? menu.path : '';
        return {
          key: id,
          primary,
          secondary,
          tag: menu?.menuId && menu?.menuId !== id ? menu.menuId : undefined,
          tagColor: 'purple'
        };
      });
    }

    if (group.groupType === 'API_GROUP') {
      return ids.map((rawId) => {
        const id = String(rawId);
        const api = apiLookup[id];
        const method = api?.methodType;
        const url = api?.apiUrl;
        const methodUrl = [method, url].filter(Boolean).join(' ');
        const primary = api?.apiNm || methodUrl || id;
        const secondary = methodUrl && methodUrl !== primary ? methodUrl : '';
        return {
          key: id,
          primary,
          secondary,
          tag: method || undefined,
          tagColor: 'geekblue'
        };
      });
    }

    return ids.map((rawId) => {
      const id = String(rawId);
      return {
        key: id,
        primary: id,
        secondary: '',
        tag: undefined
      };
    });
  }, [userLookup, menuLookup, apiLookup]);

  const renderGroupSection = useCallback((group, heading) => {
    if (!group) return null;

    const sectionTitle = heading || `${TYPE_LABEL[group.groupType] || '그룹'} 상세`;
    const members = getGroupMembers(group);
    const memberTitle = group.groupType === 'USER_GROUP'
      ? '사용자 목록'
      : group.groupType === 'MENU_GROUP'
        ? '메뉴 목록'
        : group.groupType === 'API_GROUP'
          ? 'API 목록'
          : '대상 목록';

    return (
      <>
        <Divider orientation="left">{sectionTitle}</Divider>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="그룹명">{group.groupNm}</Descriptions.Item>
            <Descriptions.Item label="그룹 유형">
              <Tag color={TYPE_COLOR[group.groupType] || 'default'}>
                {TYPE_LABEL[group.groupType] || group.groupType}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="설명">{group.groupDsctn || '-'}</Descriptions.Item>
          </Descriptions>

          {lookupLoading && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              padding: '16px 0',
              gap: 8
            }}>
              <Spin size="small" />
              <Text type="secondary">멤버 정보를 불러오는 중...</Text>
            </div>
          )}

          {!lookupLoading && (
            <>
              <Divider style={{ margin: '12px 0' }}>
                {memberTitle} ({members.length})
              </Divider>

              {members.length ? (
                <List
                  size="small"
                  dataSource={members}
                  style={{ maxHeight: 320, overflowY: 'auto' }}
                  renderItem={(member) => {
                    if (group.groupType === 'USER_GROUP') {
                      return (
                        <List.Item 
                          key={member.key}
                          style={{ 
                            padding: '8px 12px',
                            borderRadius: 4,
                            transition: 'background 0.2s',
                            cursor: 'default'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <Space size={8} align="start" style={{ width: '100%' }}>
                            <Avatar 
                              size={32} 
                              icon={<UserOutlined />} 
                              style={{ 
                                backgroundColor: member.tagColor || '#1890ff',
                                flexShrink: 0
                              }}
                            />
                            <Space direction="vertical" size={0} style={{ flex: 1, minWidth: 0 }}>
                              <Text strong style={{ fontSize: 13 }}>
                                {member.primary}
                              </Text>
                              {member.secondary && (
                                <Text 
                                  type="secondary" 
                                  style={{ fontSize: 11 }}
                                >
                                  {member.secondary}
                                </Text>
                              )}
                              {member.tag && (
                                <Tag 
                                  color="processing" 
                                  style={{ 
                                    marginTop: 4, 
                                    fontSize: 11,
                                    lineHeight: '18px',
                                    padding: '0 6px'
                                  }}
                                >
                                  ID: {member.tag}
                                </Tag>
                              )}
                            </Space>
                          </Space>
                        </List.Item>
                      );
                    }

                    if (group.groupType === 'MENU_GROUP') {
                      return (
                        <List.Item 
                          key={member.key}
                          style={{ 
                            padding: '6px 12px',
                            borderRadius: 4,
                            transition: 'background 0.2s',
                            cursor: 'default'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <Space size={8} align="start" style={{ width: '100%' }}>
                            <MenuOutlined style={{ 
                              color: member.tagColor || '#722ed1',
                              fontSize: 16,
                              marginTop: 2
                            }} />
                            <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                              <Space size={6} wrap>
                                <Text strong style={{ fontSize: 13 }}>
                                  {member.primary}
                                </Text>
                                {member.tag && (
                                  <Tag 
                                    color={member.tagColor || 'purple'}
                                    style={{ fontSize: 11 }}
                                  >
                                    {member.tag}
                                  </Tag>
                                )}
                              </Space>
                              {member.secondary && (
                                <Text 
                                  type="secondary" 
                                  style={{ fontSize: 11 }}
                                >
                                  <LinkOutlined style={{ marginRight: 4 }} />
                                  {member.secondary}
                                </Text>
                              )}
                            </Space>
                          </Space>
                        </List.Item>
                      );
                    }

                    if (group.groupType === 'API_GROUP') {
                      const methodColors = {
                        'GET': 'green',
                        'POST': 'blue',
                        'PUT': 'orange',
                        'PATCH': 'gold',
                        'DELETE': 'red'
                      };
                      
                      return (
                        <List.Item 
                          key={member.key}
                          style={{ 
                            padding: '6px 12px',
                            borderRadius: 4,
                            transition: 'background 0.2s',
                            cursor: 'default'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <Space size={8} align="start" style={{ width: '100%' }}>
                            <ApiOutlined style={{ 
                              color: member.tagColor || '#1890ff',
                              fontSize: 16,
                              marginTop: 2
                            }} />
                            <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                              <Space size={6} wrap align="center">
                                {member.tag && (
                                  <Tag 
                                    color={methodColors[member.tag] || member.tagColor}
                                    style={{ 
                                      fontSize: 11,
                                      fontWeight: 600,
                                      margin: 0
                                    }}
                                  >
                                    {member.tag}
                                  </Tag>
                                )}
                                <Text strong style={{ fontSize: 13 }}>
                                  {member.primary}
                                </Text>
                              </Space>
                              {member.secondary && (
                                <Text 
                                  type="secondary" 
                                  style={{ 
                                    fontSize: 11,
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all'
                                  }}
                                >
                                  {member.secondary}
                                </Text>
                              )}
                            </Space>
                          </Space>
                        </List.Item>
                      );
                    }

                    // Fallback for other types
                    return (
                      <List.Item key={member.key}>
                        <Space size={8} wrap>
                          {member.tag && (
                            <Tag color={member.tagColor || 'blue'}>{member.tag}</Tag>
                          )}
                          <Text>{member.primary}</Text>
                        </Space>
                        {member.secondary && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {member.secondary}
                          </Text>
                        )}
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '24px 16px',
                  color: '#00000040'
                }}>
                  <Text type="secondary">등록된 항목이 없습니다</Text>
                </div>
              )}
            </>
          )}
        </Card>
      </>
    );
  }, [getGroupMembers, lookupLoading]);

  const drawerTitle = isNew ? '권한 등록' : (editMode ? '권한 수정' : '권한 상세');

  const renderHeaderActions = () => {
    if (isNew) {
      return (
        <Space>
          <PermissionButton
            type="primary"
            icon={<SaveOutlined/>}
            onClick={() => form.submit()}
            loading={saving}
            require={createAuthority}
            size="small"
          >
            저장
          </PermissionButton>
          <Button size="small" onClick={onClose}>취소</Button>
        </Space>
      );
    }

    if (editMode) {
      return (
        <Space>
          <PermissionButton
            type="primary"
            icon={<SaveOutlined/>}
            onClick={() => form.submit()}
            loading={saving}
            require={updateAuthority}
            size="small"
          >
            저장
          </PermissionButton>
          <Button size="small" onClick={handleCancelEdit}>취소</Button>
        </Space>
      );
    }

    return (
      <Space>
        <PermissionButton 
          type="primary"
          icon={<EditOutlined />}
          onClick={handleEdit} 
          require={updateAuthority}
          size="small"
        >
          수정
        </PermissionButton>
        <Popconfirm
          title="권한 삭제"
          description="이 권한을 삭제하시겠습니까?"
          onConfirm={handleDelete}
          okText="삭제"
          okType="danger"
          cancelText="취소"
        >
          <PermissionButton 
            danger
            icon={<DeleteOutlined />}
            require={deleteAuthority}
            size="small"
          >
            삭제
          </PermissionButton>
        </Popconfirm>
      </Space>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{display: 'flex', justifyContent: 'center', padding: '40px 0'}}>
          <Spin/>
        </div>
      );
    }

    if (editMode || isNew) {
      return (
        <>
          <Form form={form} layout="vertical" onFinish={handleSave}>
            <Form.Item
              label="사용자 그룹"
              name="userGroupId"
              rules={[{required: true, message: '사용자 그룹을 선택하세요.'}]}
            >
              <Select
                placeholder="사용자 그룹 선택"
                options={userGroups.map(g => ({label: g.groupNm, value: g.groupId}))}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              label="대상 그룹 유형"
              name="trgtGroupType"
              rules={[{required: true, message: '대상 그룹 유형을 선택하세요.'}]}
            >
              <Select
                placeholder="대상 그룹 유형 선택"
                onChange={() => form.setFieldValue('trgtGroupId', undefined)}
                options={[
                  {label: '메뉴그룹', value: 'MENU_GROUP'},
                  {label: 'API그룹', value: 'API_GROUP'}
                ]}
              />
            </Form.Item>

            <Form.Item
              label="대상 그룹"
              name="trgtGroupId"
              rules={[{required: true, message: '대상 그룹을 선택하세요.'}]}
            >
              <Select
                placeholder="대상 그룹 선택"
                options={trgtGroupOptions}
                showSearch
                optionFilterProp="label"
                disabled={!trgtGroupType}
              />
            </Form.Item>
          </Form>

          {renderGroupSection(selectedUserGroup, '사용자 그룹 상세')}
          {renderGroupSection(selectedTrgtGroup, '대상 그룹 상세')}
        </>
      );
    }

    // 상세 보기 모드
    const userGroup = userGroups.find(g => g.groupId === record?.userGroupId);
    const trgtGroup = [...menuGroups, ...apiGroups].find(g => g.groupId === record?.trgtGroupId);

    return (
      <>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="권한 ID">{record?.authrtId}</Descriptions.Item>
          <Descriptions.Item label="사용자 그룹">
            {userGroup ? userGroup.groupNm : record?.userGroupId || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="대상 그룹 유형">
            <Tag color={TYPE_COLOR[record?.trgtGroupType] || 'default'}>
              {TYPE_LABEL[record?.trgtGroupType] || record?.trgtGroupType || '-'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="대상 그룹">
            {trgtGroup ? trgtGroup.groupNm : record?.trgtGroupId || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="등록자">{record?.rgtr || '-'}</Descriptions.Item>
          <Descriptions.Item label="등록일시">{record?.regDt || '-'}</Descriptions.Item>
          <Descriptions.Item label="수정자">{record?.updr || '-'}</Descriptions.Item>
          <Descriptions.Item label="수정일시">{record?.updDt || '-'}</Descriptions.Item>
        </Descriptions>

        {renderGroupSection(userGroup, '사용자 그룹 상세')}
        {renderGroupSection(trgtGroup, '대상 그룹 상세')}

        <Divider/>
      </>
    );
  };

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 24 }}>
          <span>{drawerTitle}</span>
          {renderHeaderActions()}
        </div>
      }
      open={open}
      onClose={onClose}
      width={700}
      footer={null}
      destroyOnClose
    >
      {renderContent()}
    </Drawer>
  );
};

export default AuthorityDetailDrawer;
